import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";

// MUI Icons
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";

// Components
import { EmissionActivityDeleteModal } from "../modals/EmissionActivity/EmissionActivityDeleteModal";
import { EmissionActivityExcelModal } from "../modals/EmissionActivity/EmissionActivityExcelModal";

// Utils
import { flatToNested } from "../utils/ToNestEAFormData";
import { getEASchemaByStandard } from "../modals/utils/getEASchemaByStandard";
import { getEFSchemaByStandard } from "../modals/utils/getEFSchemaByStandard";
import { getConstByStandard } from "../utils/getConstFileByStandard";

// Stores
import emissionViewStore from "../../Store/marco/emissionViewStore";
import commonStore from "../../Store/marco/commonStore";

// Styles
import styles from "../styles/emissionActivity.module.css";

/** Special industry code that requires hiding actions */
const SPECIAL_INDUSTRY_CODE = "8550";

// for testing YouanYouan
const SPECIAL_ACCOUNT = null;

// Marco test
// const SPECIAL_ACCOUNT = "684fbbb625760e000b11edd7";

/** Category codes that require special handling */
const CATEGORY_CODES = {
  CAT_4_3: "4_3",
  CAT_5_1: "5_1",
};

/** Mode ID values for transportation types */
const MODE_IDS = {
  TRANSPORTATION: 1,
  ACCOMMODATION: 2,
};

/** Calculation method IDs */
const CALCULATION_IDS = {
  WEIGHT_DISTANCE: 0,
  FUEL: 1,
};

const EF_MAIN_CONTENT_TITLES = new Set([
  "EF Database Source",
  "Fuel Name / Vehicle Type",
  "Energy Type",
  "Emission Source",
  "Transportation Mode",
]);

const shouldUseEFMainContent = (columnTitle, categoryLabel) => {
  const isCategory2 = String(categoryLabel || "").startsWith("2.");
  if (columnTitle === "Energy Type" && isCategory2) return false;

  return EF_MAIN_CONTENT_TITLES.has(columnTitle);
};

/** Color for frozen/verifying state */
const VERIFYING_COLOR = "rgb(20, 100, 200)";

/**
 * Field mapping for Emission Activity (EA) data transformation
 * Maps backend field names to form field names
 */
const EA_FIELD_MAPPING = {
  isMassTransit: 10,
  fugitiveEmissions: 11,
  companyId: "selectedCom",
  id: "id",
  isDirectApiEntry: "isDirectApiEntry",
};

/**
 * Field mapping for Emission Factor (EF) data transformation
 * Maps backend field names to form field names
 */
const EF_FIELD_MAPPING = {
  companyId: "selectedCom",
  id: "id",
};

/**
 * Fields to copy from EA to EF data for category 5_1
 */
const CAT_5_1_FIELDS_TO_COPY = [
  "energySupplier",
  "customEnergyName",
  "wasteManagementVendorName",
  "calculationId",
];

const checkNoPermission = (hideAction, hasAccess, industryCode, rootId) => {
  return (
    hideAction ||
    !hasAccess ||
    industryCode == SPECIAL_INDUSTRY_CODE ||
    rootId == SPECIAL_ACCOUNT
  );
};

const getTableConfig = (CONST, selectedTab) => {
  const config = CONST.tableTitles?.find((item) => item.label === selectedTab);
  return {
    titles: config?.titles || [],
    dataKey: config?.dataKey || [],
  };
};

const transformAndUpdateFormData = (
  sourceData,
  schemaDef,
  fieldMapping,
  updateFunction,
  formKey,
  additionalData = {},
) => {
  const nestedData = flatToNested(sourceData, schemaDef, fieldMapping);
  const finalData = { ...nestedData, ...additionalData };
  updateFunction(finalData, formKey);
};

const processEAFormData = ({ EAData, schema, isCat43, updateFunction }) => {
  if (isCat43) {
    // Category 4_3 has both normal and transportation forms
    transformAndUpdateFormData(
      EAData,
      schema.normal,
      EA_FIELD_MAPPING,
      updateFunction,
    );
    transformAndUpdateFormData(
      EAData,
      schema.tran,
      EA_FIELD_MAPPING,
      updateFunction,
      "landEAFormData",
    );
  } else {
    // Standard single form
    transformAndUpdateFormData(
      EAData,
      schema,
      EA_FIELD_MAPPING,
      updateFunction,
    );
  }
};

const processEFFormData = ({
  EAData,
  EFData,
  EFSchema,
  isCat43,
  isCat51,
  updateFunction,
}) => {
  // Copy specific fields from EA to EF for category 5_1
  if (isCat51) {
    CAT_5_1_FIELDS_TO_COPY.forEach((field) => {
      EFData[field] = EAData[field];
    });
  }

  // Prepare additional data to merge
  const additionalData = {
    customEFUnavailable: Boolean(EAData?.isEFUnavailable),
    unavailableCustomEFName:
      EAData?.emissionFactorName || EFData?.emissionFactorName || EFData?.nameEF,
  };
  if (EAData?.otherEFNote) {
    additionalData.otherEFNote = EAData.otherEFNote;
  }

  const landAdditionalData = {
    ...additionalData,
    customEFUnavailable: Boolean(EAData?.isLandEFUnavailable),
    unavailableCustomEFName:
      EAData?.landEmissionFactorName ||
      EFData?.landEmissionFactorName ||
      EFData?.landEFName,
  };

  if (isCat43) {
    // Category 4_3 has both normal and land transportation forms
    transformAndUpdateFormData(
      EFData,
      EFSchema.normal,
      EF_FIELD_MAPPING,
      updateFunction,
      undefined,
      additionalData,
    );
    transformAndUpdateFormData(
      EFData,
      EFSchema.tran,
      EF_FIELD_MAPPING,
      updateFunction,
      "EFLandFormData",
      landAdditionalData,
    );
  } else {
    // Standard single form
    transformAndUpdateFormData(
      EFData,
      EFSchema,
      EF_FIELD_MAPPING,
      updateFunction,
      undefined,
      additionalData,
    );
  }
};

const renderTableCell = (
  item,
  fieldKey,
  columnTitle,
  categoryLabel,
  t,
  tableDataKeys = [],
  t1,
) => {
  const cellValue = item[fieldKey];
  const isEFDatabaseSourceField = fieldKey.startsWith("sourceEFName");

  switch (fieldKey) {
    case "modeId":
      if (cellValue === MODE_IDS.TRANSPORTATION) {
        return <td>{t("Transportation")}</td>;
      }
      if (cellValue === MODE_IDS.ACCOMMODATION) {
        return <td>{t("Accommodation")}</td>;
      }
      return null; // Don't render td for other values

    case "calculationId":
      return (
        <td>
          {cellValue === CALCULATION_IDS.WEIGHT_DISTANCE
            ? t("Weight Distance")
            : cellValue === CALCULATION_IDS.FUEL
              ? t("Fuel")
              : "-"}
        </td>
      );

    case "distance":
      return (
        <td>{item.modeId === MODE_IDS.ACCOMMODATION ? "-" : t(cellValue)}</td>
      );

    case "goodsName": {
      // Purchased Services (4_5) stores name in itemName instead of goodsName
      // Fall back to itemName when goodsName is empty
      const goodsDisplayName = cellValue || item["itemName"];
      return goodsDisplayName ? <td>{t(goodsDisplayName)}</td> : <td>-</td>;
    }

    case "emissionFactorName":
    case "computationMethod": {
      return cellValue ? <td>{t1(cellValue)}</td> : <td>-</td>;
    }

    case "itemName": {
      // Only apply deduplication when the table has both goodsName and itemName columns
      // (i.e., 3.1 Purchased Goods and Services)
      const tableHasGoodsNameKey = tableDataKeys.includes("goodsName");
      if (tableHasGoodsNameKey) {
        // If goodsName was empty (Purchased Services), itemName was already used
        // as fallback for the "Purchased Goods / Services Name" column,
        // so show "-" here to avoid displaying the same value twice
        const hasGoodsName = !!item["goodsName"];
        const itemDisplayValue = hasGoodsName ? cellValue : null;
        return itemDisplayValue ? <td>{t(itemDisplayValue)}</td> : <td>-</td>;
      }
      // For other categories, render itemName normally
      return cellValue ? <td>{t(cellValue)}</td> : <td>-</td>;
    }

    case "fugitiveEmissionsOrValue":
      return cellValue ? <td>{t(cellValue)} %</td> : <td>-</td>;

    default:
      if (isEFDatabaseSourceField) {
        return (
          <td>
            {cellValue ? (
              <div className={styles.efSourceCell}>
                <span>{t1(cellValue)}</span>
                {Number(item.efId) > 0 ? (
                  <span className={styles.efIdBadge}>{item.efId}</span>
                ) : null}
              </div>
            ) : (
              "-"
            )}
          </td>
        );
      }

      if (shouldUseEFMainContent(columnTitle, categoryLabel)) {
        return cellValue ? <td>{t1(cellValue)}</td> : <td>-</td>;
      }

      return cellValue ? <td>{t(cellValue)}</td> : <td>-</td>;
  }
};

export const EmissionActivity = observer(() => {
  // -------------------------------------------------------------------------
  // Stores & Hooks
  // -------------------------------------------------------------------------
  const store = emissionViewStore;
  const cmStore = commonStore;
  const { t } = useTranslation("ActivityMain");
  const { t: t1 } = useTranslation("EFMain");

  // -------------------------------------------------------------------------
  // Derived Values
  // -------------------------------------------------------------------------
  const CONST = getConstByStandard(store.standardId);
  const EASchema = getEASchemaByStandard(store.standardId)[store.selectedTab];
  const EFSchema = getEFSchemaByStandard(store.standardId)[store.selectedTab];

  // Find current scope configuration
  const currentScopeConfig = cmStore?.EACategory?.find(
    (item) => item.label === store.selectedScope,
  );

  // Check if actions are disabled for current category
  const isActionDisabled = currentScopeConfig?.category?.find(
    (item) => item.label === store.selectedTab,
  )?.isDisabled;

  // Permission checks
  const hasNoPermission = checkNoPermission(
    isActionDisabled,
    cmStore.access,
    cmStore.industryCode,
    cmStore.rootId,
  );

  // Data state checks
  const isLoading = store.emissionActivityListLoading;
  const currentPageList = store.EACurrentPageList;
  const isEmpty =
    !isLoading && (!currentPageList || currentPageList.length === 0);

  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------
  const [tableTitles, setTableTitles] = useState([]);
  const [tableDataKeys, setTableDataKeys] = useState([]);

  // -------------------------------------------------------------------------
  // Effects
  // -------------------------------------------------------------------------

  // Fetch emission activity list when dependencies change
  useEffect(() => {
    store._handleSetEmissionActivityListData("fullList", []);

    if (isActionDisabled) return;
    if (store.authComArr.length > 0) {
      store.getEAListData({ keepPage: true });
    }
  }, [
    store.selectedScope,
    store.selectedTab,
    store.authComArr,
    store.isRefresh,
    store.searchKeyword,
  ]);

  // Initialize table configuration when scope or tab changes
  useEffect(() => {
    const { titles, dataKey } = getTableConfig(CONST, store.selectedTab);
    setTableTitles(titles);
    setTableDataKeys(dataKey);

    // Reset pagination to first page
    store._handleSetPageNow(1);
  }, [store.selectedScope, store.selectedTab]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleEditEmissionActivity = async (id, categoryISO64) => {
    store._handleSetEmissionActivityModalLoading(true);

    try {
      if (!EFSchema) {
        throw new Error("EFSchema not found");
      }

      store._handleSetEditId(id);

      // Determine category type
      const isCat43 = EFSchema.cat === CATEGORY_CODES.CAT_4_3;
      const isCat51 = EFSchema.cat === CATEGORY_CODES.CAT_5_1;

      // Fetch EA and EF data
      const EAData = await store.getSingleEAData(id, categoryISO64);
      const EFData = await store.getSingleEFData(id);

      // Process EA form data
      processEAFormData({
        EAData,
        schema: EASchema,
        isCat43,
        updateFunction: store._handleEditEAFormData,
      });

      // Process EF form data
      processEFFormData({
        EAData,
        EFData,
        EFSchema,
        isCat43,
        isCat51,
        updateFunction: store._handleEditEFFormData,
      });

      // Set edit mode
      store._handleSetIsEditEmissionActivity(true);
    } catch (err) {
      console.error("Edit emission activity error:", err);
    } finally {
      store._handleSetEmissionActivityModalLoading(false);
    }
  };

  const handleDeleteEmissionActivity = async (id) => {
    const deleteSuccess = await store.deleteEAData(id);
    if (deleteSuccess) {
      await store.getEAListData({ keepPage: true });
    }
  };

  // -------------------------------------------------------------------------
  // Render Helpers
  // -------------------------------------------------------------------------

  const renderActionCell = (item) => {
    if (hasNoPermission) {
      return null;
    }

    // Show verification message for frozen items
    if (item.isFrozen) {
      return (
        <td>
          <span style={{ color: VERIFYING_COLOR }}>
            {t("Verifying data...")}
          </span>
        </td>
      );
    }

    // Show edit and delete buttons
    return (
      <td>
        <span
          className={styles.edit}
          onClick={async () => {
            store._handleSetIsEditEmissionActivity(true);
            store._handleShowEmissionActivityModal(true);
            await handleEditEmissionActivity(item.id, item.categoryISO64);
          }}
        >
          <EditIcon />
        </span>

        {item.isDirectApiEntry ? null : (
          <span
            className={styles.delete}
            onClick={() =>
              store._handleSetConfirmDeleteEmissionActivityModal(
                item.id,
                item.itemName,
                true,
                item.isFrozen,
              )
            }
          >
            <DeleteForeverIcon />
          </span>
        )}
      </td>
    );
  };

  const renderTableOverlay = () => {
    if (isActionDisabled) {
      return (
        <div className={styles.noData}>
          <p>{t("This activity has not been disclosed")}</p>
        </div>
      );
    }

    if (isEmpty) {
      return (
        <div className={styles.noData}>
          <p>{t("No data!")}</p>
        </div>
      );
    }

    return null;
  };

  const renderModals = () => (
    <>
      {store.confirmDeleteEmissionActivityData.isShowModal && (
        <EmissionActivityDeleteModal
          handleDelete={handleDeleteEmissionActivity}
        />
      )}

      {store.isShowEmissionActivityExcelModal && <EmissionActivityExcelModal />}
    </>
  );

  const renderTableHeader = () => (
    <thead>
      <tr>
        <th style={{ width: "60px" }}>{t("No.")}</th>

        {tableTitles?.map((title, index) => (
          <th
            key={title}
            style={index === 0 ? { width: "300px" } : { minWidth: "120px" }}
          >
            {t(title)}
          </th>
        ))}

        {!hasNoPermission && <th style={{ width: "100px" }}>{t("Actions")}</th>}
      </tr>
    </thead>
  );

  const renderTableBody = () => (
    <tbody>
      {currentPageList?.map((item, index) => (
        <tr key={item.id}>
          <td>
            {(store.emissionActivityListData.page - 1) *
              store.emissionActivityListData.pageSize +
              index +
              1}
          </td>
          <td>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                width: "100%",
                paddingRight: "8px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: "8px",
                }}
              >
                <span
                  style={{
                    fontSize: "15px",
                    color: "#333",
                    fontWeight: 500,
                    wordBreak: "break-word",
                  }}
                >
                  {item.companyName}
                </span>
              </div>
              {/* 這是 mock 的母公司/階層路徑排版 */}
              <div
                style={{
                  fontSize: "13px",
                  color: "#999",
                  wordBreak: "break-word",
                  textAlign: "left",
                }}
              >
                {t(item.hierarchyNamePath)}
              </div>
            </div>
          </td>

          {tableDataKeys?.map((key, index) => (
            <React.Fragment key={key}>
              {renderTableCell(
                item,
                key,
                tableTitles[index + 1],
                store.selectedTab,
                t,
                tableDataKeys,
                t1,
              )}
            </React.Fragment>
          ))}

          {!hasNoPermission && renderActionCell(item)}
        </tr>
      ))}
    </tbody>
  );

  // -------------------------------------------------------------------------
  // Main Render
  // -------------------------------------------------------------------------

  return (
    <div className={styles.container}>
      <table className={styles.table}>
        {renderTableHeader()}
        {renderTableBody()}
      </table>

      {renderTableOverlay()}
      {renderModals()}
    </div>
  );
});
